import type {
  INewActivity,
  INewAdminTaskActivity,
  INewClinicVisitActivity,
  INewHomeVisitActivity,
  INewPatientMeasurementTaskActivity,
  INewPatientTaskActivity,
  INewPhoneCallActivity,
  INewVideoActivity,
} from "@/api/Activities";
import { ANY_TIME_OF_DAY } from "@/api/Activities";
import {
  activityFrequencySchema,
  recurringOptionSchema,
} from "@/forms/AddActivity/Scheduling/schedulingModels";
import { generateRandomUUID } from "@/Utils/UniqueId";
import {
  calendarDateSchema,
  dateInputSchema,
} from "@medoma/models/date-and-time";
import {
  getTimespan,
  getTimeFields,
} from "@/forms/AddActivity/activityTimeUtils";
import {
  categorySchema,
  timeOfDaySchema,
  videoTypeSchema,
  type IActivityCategory,
} from "@medoma/models/activities";
import { convertDurationToMinutes } from "@/forms/AddActivity/activityDurationUtils";
import type { CreateActivityFromTemplateFormFields } from "./createActivityFromTemplateFormSchema";

export const newActivityFromActivityTemplate = ({
  formData,
  title,
  category,
  templateId,
  templateRevision,
  patientId,
}: {
  formData: CreateActivityFromTemplateFormFields;
  title: string;
  category: IActivityCategory;
  templateId: string;
  templateRevision: number;
  patientId: string;
}): INewActivity => {
  const {
    description,
    doubleStaffing,
    frequency,
    recurrence,
    customRecurrenceInterval,
    customRecurrenceIntervalUnit,
    requiredCompetences,
    timeCategory,
    weekdays,
    hidden,
    measurements,
    timeslots,
    timeSensitivity,
    startDate,
    endDate,
    durationOption,
    customDuration,
    customDurationUnit,
  } = formData;

  const duration = convertDurationToMinutes(
    durationOption ?? "15",
    customDuration ?? null,
    customDurationUnit ?? null,
  );

  const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
  const isRecurring =
    frequency === activityFrequencySchema.enum.recurring && recurrence !== null;
  const isRecurringAsInterval =
    recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

  const commonActivityFields: Pick<
    INewActivity,
    | "activityId"
    | "category"
    | "description"
    | "duration"
    | "hidden"
    | "startDate"
    | "timespan"
    | "title"
  > = {
    activityId: generateRandomUUID(),
    category,
    description: description?.trim() ?? null,
    duration,
    hidden: hidden === true || false,
    startDate: calendarDateSchema.decode(startDate),
    timespan: getTimespan({
      isAnyTimeOfDay,
      timeSensitivity: timeSensitivity ?? undefined,
    }),
    title: title.trim(),
  };

  const recurringTimeOfDayFields = isAnyTimeOfDay
    ? {
        isAnyTimeOfDay: true as const,
        timeslots: undefined,
      }
    : {
        isAnyTimeOfDay: false as const,
        timeslots: timeslots as {
          time: string;
        }[],
      };

  const timeFields = isRecurring
    ? {
        ...getTimeFields({
          isRecurringAsInterval,
          weekdays: weekdays ?? undefined,
          recurrence: recurrence!,
          customRecurrenceInterval: customRecurrenceInterval ?? null,
          customRecurrenceIntervalUnit: customRecurrenceIntervalUnit ?? null,
          ...recurringTimeOfDayFields,
        }),
        endDate: dateInputSchema.decode(endDate ?? "") ?? undefined,
      }
    : // @ts-expect-error we know that there is exactly one timeslot, but TS doesn't.
      { time: isAnyTimeOfDay ? ANY_TIME_OF_DAY : timeslots[0].time };

  switch (category) {
    case categorySchema.enum.VideoCall: {
      const newVideoActivity: INewVideoActivity = {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.VideoCall,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        type: videoTypeSchema.enum.DigitalVisit,
        templateId,
        templateRevision,
      };
      return newVideoActivity;
    }
    case categorySchema.enum.HomeVisit: {
      const newHomeVisitActivity: INewHomeVisitActivity = {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.HomeVisit,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        doubleStaffing: doubleStaffing ?? false,
        templateId,
        templateRevision,
      };
      return newHomeVisitActivity;
    }
    case categorySchema.enum.ClinicVisit: {
      const newClinicVisitActivity: INewClinicVisitActivity = {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.ClinicVisit,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        templateId,
        templateRevision,
      };
      return newClinicVisitActivity;
    }
    case categorySchema.enum.PatientTask: {
      const newPatientTaskActivity: INewPatientTaskActivity = {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.PatientTask,
        ...timeFields,
        templateId,
        templateRevision,
      };
      return newPatientTaskActivity;
    }
    case categorySchema.enum.AdminTask: {
      const newAdminTaskActivity: INewAdminTaskActivity = {
        ...commonActivityFields,
        patientId: patientId ? patientId : null,
        category: categorySchema.enum.AdminTask,
        requiredCompetences: requiredCompetences ? requiredCompetences : [],
        ...timeFields,
        templateId,
        templateRevision,
      };
      return newAdminTaskActivity;
    }
    case categorySchema.enum.PatientMeasurementTask: {
      const newPatientMeasurementTaskActivity: INewPatientMeasurementTaskActivity =
        {
          ...commonActivityFields,
          patientId,
          category: categorySchema.enum.PatientMeasurementTask,
          measurements: measurements ?? [],
          ...timeFields,
          templateId,
          templateRevision,
        };
      return newPatientMeasurementTaskActivity;
    }
    case categorySchema.enum.PhoneCall: {
      const newPhoneCallActivity: INewPhoneCallActivity = {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.PhoneCall,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        templateId,
        templateRevision,
      };
      return newPhoneCallActivity;
    }
    default:
      throw category satisfies never;
  }
};
